'use client'

import { Card, BlockStack, Text, Button, ProgressBar, SkeletonDisplayText, InlineStack } from '@shopify/polaris'
import { useSigmaSurface } from '@/components/SigmaSurfaceProvider'

function capitalizePlan(plan: string): string {
  if (!plan) return 'Free'
  return plan.charAt(0).toUpperCase() + plan.slice(1).toLowerCase()
}

interface UsageBarProps {
  plan?: string
  usedOrders: number
  orderLimit: number | null
  percentUsed: number
  loading?: boolean
  billingHref?: string
}

export function UsageBar({
  plan,
  usedOrders,
  orderLimit,
  percentUsed,
  loading = false,
  billingHref = '/billing',
}: UsageBarProps) {
  const isPortal = useSigmaSurface() === 'portal'
  const pctLabel = `${Math.round(Math.min(percentUsed, 100))}%`

  if (loading || orderLimit === null) {
    return (
      <Card>
        <BlockStack gap="200">
          <Text as="h3" variant="headingSm">
            Orders used this month
          </Text>
          <SkeletonDisplayText size="small" />
          <SkeletonDisplayText size="medium" />
        </BlockStack>
      </Card>
    )
  }

  const isWarning = percentUsed >= 80 && percentUsed < 95
  const isCritical = percentUsed >= 95
  const progressTone: 'critical' | 'highlight' | undefined = isCritical ? 'critical' : isWarning ? 'highlight' : undefined

  const remaining = Math.max(0, orderLimit - usedOrders)

  return (
    <Card>
      <BlockStack gap="300">
        {plan && (
          <Text as="h3" variant="headingSm">
            {capitalizePlan(plan)} Plan
          </Text>
        )}
        <Text as="h3" variant="headingSm">
          Orders used this month
        </Text>
        <Text as="p" variant="bodyMd">
          {usedOrders.toLocaleString()} / {orderLimit.toLocaleString()} orders used
        </Text>
        <Text as="p" variant="bodySm" tone="subdued">
          {remaining.toLocaleString()} orders remaining this month
        </Text>
        {isPortal ? (
          <InlineStack gap="300" blockAlign="center" wrap={false}>
            <div style={{ flex: '1 1 0', minWidth: 0 }}>
              <ProgressBar progress={Math.min(percentUsed, 100)} tone={progressTone} size="small" />
            </div>
            <Text as="span" variant="bodyMd" fontWeight="semibold">
              {pctLabel}
            </Text>
          </InlineStack>
        ) : (
          <ProgressBar progress={Math.min(percentUsed, 100)} tone={progressTone} size="small" />
        )}
        {isWarning && percentUsed < 100 && (
          <Text as="p" variant="bodySm" tone="caution">
            You are close to your monthly order limit.
          </Text>
        )}
        {isCritical && (
          <BlockStack gap="200">
            <Text as="p" variant="bodySm" tone="critical">
              {percentUsed >= 100
                ? 'You have reached your monthly order limit.'
                : 'You are very close to your monthly order limit.'}
            </Text>
            <Button url={billingHref} variant="primary" size="slim">
              Upgrade Plan
            </Button>
          </BlockStack>
        )}
      </BlockStack>
    </Card>
  )
}
